import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useCurrentCompany } from "../components/CompanySwitcher";
import { fmtMoney, fmtDate } from "../lib/format";

interface ClientRow {
  id: number; name: string; contact_name: string | null; email: string | null;
  language: "en" | "th"; credit_terms_days: number; notes: string | null;
  open_count: number; open_total: number; last_sent_at: string | null;
}

interface Invoice {
  id: number;
  number: string;
  issue_date: string;
  amount: number;
  status: string;
  memo: string | null;
  collection_status: "open" | "paid" | "written_off" | "ignored";
  marked_paid_at: string | null;
}

export default function Clients() {
  const { current } = useCurrentCompany();
  const [rows, setRows] = useState<ClientRow[]>([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState<ClientRow | null>(null);
  const [adding, setAdding] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  async function refresh() {
    if (!current) return;
    const r = await api<{ clients: ClientRow[] }>(`/api/clients?company_id=${current.id}`);
    setRows(r.clients);
  }
  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [current?.id]);

  const filtered = rows.filter((c) =>
    !q || c.name.toLowerCase().includes(q.toLowerCase()) || (c.email || "").toLowerCase().includes(q.toLowerCase())
  );

  const currency = current?.currency || "THB";

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tightish text-slate-900">Clients</h1>
          <p className="mt-1 text-sm text-slate-500">{rows.length} total · {rows.filter((r) => r.open_count > 0).length} with open balance</p>
        </div>
        <button className="btn-primary" onClick={() => setAdding(true)}>
          <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor"><path d="M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z" /></svg>
          New client
        </button>
      </div>

      <div className="flex items-center gap-2">
        <div className="relative w-96">
          <svg className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" /></svg>
          <input className="search-input w-full" placeholder="Search by name or email…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      </div>

      <div className="card overflow-hidden">
        <table className="refined">
          <thead>
            <tr>
              <th className="w-8 pl-3 pr-0"></th>
              <th>Client</th>
              <th>Email</th>
              <th>Lang</th>
              <th>Terms</th>
              <th>Open</th>
              <th className="text-right">Open total</th>
              <th>Last sent</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <ClientRowView
                key={c.id}
                client={c}
                expanded={expandedId === c.id}
                onToggle={() => setExpandedId(expandedId === c.id ? null : c.id)}
                onEdit={() => setEditing(c)}
                onChanged={refresh}
                currency={currency}
              />
            ))}
            {!filtered.length && (
              <tr><td colSpan={9} className="px-4 py-12 text-center text-slate-500">
                {rows.length ? "No matches" : "No clients yet — import a QBO file or add manually."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {(editing || adding) && current && (
        <EditDialog
          companyId={current.id}
          client={editing}
          onClose={() => { setEditing(null); setAdding(false); }}
          onSaved={() => { setEditing(null); setAdding(false); refresh(); }}
        />
      )}
    </div>
  );
}

function ChevronRight({ open }: { open: boolean }) {
  return (
    <svg
      className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-90" : ""}`}
      viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"
    >
      <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
    </svg>
  );
}

function ClientRowView({
  client, expanded, onToggle, onEdit, onChanged, currency,
}: {
  client: ClientRow;
  expanded: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onChanged: () => void;
  currency: string;
}) {
  return (
    <>
      <tr className={expanded ? "bg-slate-50/60" : ""}>
        <td className="pl-3 pr-0">
          <button type="button" onClick={onToggle} className="btn-ghost !p-1.5"
                  aria-label={expanded ? "Collapse" : "Expand"}
                  title={expanded ? "Hide invoices" : "Show invoices"}>
            <ChevronRight open={expanded} />
          </button>
        </td>
        <td>
          <button onClick={onToggle} className="text-left font-medium text-slate-900 hover:text-indigo-700">
            {client.name}
          </button>
          {client.contact_name && <div className="text-xs text-slate-500">{client.contact_name}</div>}
        </td>
        <td className="text-slate-700">
          {client.email || <span className="text-rose-500">— missing —</span>}
        </td>
        <td><span className="pill pill-slate uppercase">{client.language}</span></td>
        <td className="text-slate-600">{client.credit_terms_days}<span className="text-slate-400">d</span></td>
        <td>{client.open_count > 0
          ? <span className="font-medium text-slate-900">{client.open_count}</span>
          : <span className="text-slate-400">0</span>}</td>
        <td className="text-right font-mono font-semibold text-slate-900">
          {client.open_total > 0 ? fmtMoney(client.open_total, currency) : <span className="text-slate-300">—</span>}
        </td>
        <td className="text-xs text-slate-500">{client.last_sent_at ? fmtDate(client.last_sent_at) : "—"}</td>
        <td className="pr-3 text-right">
          <button className="btn-secondary !py-1 !px-2.5 text-xs" onClick={onEdit}>Edit</button>
        </td>
      </tr>
      {expanded && (
        <tr className="bg-slate-50/40">
          <td colSpan={9} className="px-5 py-4">
            <InvoicesPanel clientId={client.id} currency={currency} onChanged={onChanged} />
          </td>
        </tr>
      )}
    </>
  );
}

function InvoicesPanel({
  clientId, currency, onChanged,
}: { clientId: number; currency: string; onChanged: () => void }) {
  const [invoices, setInvoices] = useState<Invoice[] | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  async function load() {
    const r = await api<{ estimates: Invoice[] }>(`/api/clients/${clientId}`);
    setInvoices(r.estimates);
  }
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [clientId]);

  async function setStatus(id: number, status: Invoice["collection_status"]) {
    setBusyId(id);
    try {
      await api(`/api/estimates/${id}`, { method: "PATCH", body: JSON.stringify({ collection_status: status }) });
      await load();
      onChanged();
    } finally { setBusyId(null); }
  }

  async function remove(id: number, number: string) {
    if (!confirm(`Delete invoice ${number}? This cannot be undone (re-import to restore).`)) return;
    setBusyId(id);
    try {
      await api(`/api/estimates/${id}`, { method: "DELETE" });
      await load();
      onChanged();
    } finally { setBusyId(null); }
  }

  if (invoices === null) return <div className="text-xs text-slate-500">Loading invoices…</div>;
  if (invoices.length === 0) return <div className="text-xs text-slate-500">No invoices on file for this client.</div>;

  const visible = showAll ? invoices : invoices.filter((i) => i.collection_status === "open");

  return (
    <div className="rounded-lg bg-white p-4 shadow-soft ring-1 ring-slate-900/5">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="section-title">Invoices <span className="font-normal text-slate-400">({visible.length}/{invoices.length})</span></div>
        <label className="flex items-center gap-1.5 text-xs text-slate-500">
          <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)}
                 className="h-3.5 w-3.5 rounded border-slate-300" />
          Show closed / ignored too
        </label>
      </div>
      <table className="w-full text-xs">
        <thead className="text-left">
          <tr className="border-b border-slate-100 text-[10px] uppercase tracking-wider text-slate-500">
            <th className="py-2 pr-3 font-semibold">Number</th>
            <th className="font-semibold">Date</th>
            <th className="text-right font-semibold">Amount</th>
            <th className="font-semibold">Memo</th>
            <th className="font-semibold">Status</th>
            <th className="text-right font-semibold">Actions</th>
          </tr>
        </thead>
        <tbody>
          {visible.map((inv) => (
            <tr key={inv.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60">
              <td className="py-2 pr-3 font-mono text-slate-900">{inv.number}</td>
              <td className="text-slate-700">{fmtDate(inv.issue_date)}</td>
              <td className="text-right font-mono font-semibold text-slate-900">{fmtMoney(inv.amount, currency)}</td>
              <td className="max-w-[24rem] truncate text-slate-500">{inv.memo}</td>
              <td><StatusPill status={inv.collection_status} /></td>
              <td className="space-x-1 pr-1 text-right whitespace-nowrap">
                {inv.collection_status === "open" ? (
                  <>
                    <button disabled={busyId === inv.id}
                            className="chip bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/60 hover:bg-emerald-100"
                            onClick={() => setStatus(inv.id, "paid")} title="Mark as paid">Paid</button>
                    <button disabled={busyId === inv.id}
                            className="chip bg-amber-50 text-amber-700 ring-1 ring-amber-200/60 hover:bg-amber-100"
                            onClick={() => setStatus(inv.id, "ignored")} title="Quote / not really owed">Ignore</button>
                  </>
                ) : (
                  <button disabled={busyId === inv.id}
                          className="chip bg-slate-100 text-slate-700 ring-1 ring-slate-200 hover:bg-slate-200"
                          onClick={() => setStatus(inv.id, "open")} title="Re-open">Reopen</button>
                )}
                <button disabled={busyId === inv.id}
                        className="chip bg-rose-50 text-rose-700 ring-1 ring-rose-200/60 hover:bg-rose-100"
                        onClick={() => remove(inv.id, inv.number)} title="Hard delete">Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!showAll && visible.length < invoices.length && (
        <div className="mt-2 text-[10px] text-slate-400">
          {invoices.length - visible.length} closed/ignored invoice(s) hidden.
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }: { status: Invoice["collection_status"] }) {
  const cls = {
    open:        "pill pill-blue",
    paid:        "pill pill-green",
    ignored:     "pill pill-amber",
    written_off: "pill pill-slate",
  }[status];
  return <span className={cls}>{status.replace("_", " ")}</span>;
}

function EditDialog({
  companyId, client, onClose, onSaved,
}: {
  companyId: number;
  client: ClientRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    name: client?.name || "",
    contact_name: client?.contact_name || "",
    email: client?.email || "",
    language: (client?.language || "en") as "en" | "th",
    credit_terms_days: client?.credit_terms_days ?? 30,
    notes: client?.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      if (client) {
        await api(`/api/clients/${client.id}`, { method: "PATCH", body: JSON.stringify(form) });
      } else {
        await api("/api/clients", { method: "POST", body: JSON.stringify({ ...form, company_id: companyId }) });
      }
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!client || !confirm(`Delete ${client.name}? This also removes their invoices.`)) return;
    await api(`/api/clients/${client.id}`, { method: "DELETE" });
    onSaved();
  }

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm">
      <form onSubmit={save} className="card w-full max-w-lg space-y-4 p-6">
        <div className="text-base font-semibold tracking-tightish">{client ? `Edit ${client.name}` : "New client"}</div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
          <Field label="Contact name" value={form.contact_name} onChange={(v) => setForm({ ...form, contact_name: v })} />
          <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Language</label>
            <select className="w-full" value={form.language}
                    onChange={(e) => setForm({ ...form, language: e.target.value as "en" | "th" })}>
              <option value="en">English</option>
              <option value="th">ไทย (Thai)</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">Credit terms (days)</label>
            <input type="number" className="w-full" min={0} max={365}
                   value={form.credit_terms_days}
                   onChange={(e) => setForm({ ...form, credit_terms_days: Number(e.target.value) })} />
          </div>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600">Notes</label>
          <textarea className="w-full" rows={2} value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </div>
        {err && <div className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700 ring-1 ring-rose-100">{err}</div>}
        <div className="flex items-center gap-2 pt-1">
          {client && <button type="button" className="btn-danger" onClick={remove}>Delete</button>}
          <div className="ml-auto flex items-center gap-2">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={busy}>
              {busy ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

function Field({
  label, value, onChange, type = "text", required,
}: { label: string; value: string; onChange: (v: string) => void; type?: string; required?: boolean }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-slate-600">{label}</label>
      <input type={type} className="w-full" value={value} required={required}
             onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
